import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { QvdAnalysisCoverage } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { qvdDirectoriesQueryOptions } from "@/features/qvd-analysis/api";
import { DirectoryLevel, UsageTable, type UsageRow } from "./SpaceDirectoryLevel";

// QvdFootprintBySpace ranks each space by its total footprint as a searchable,
// sortable table matching the other result lists. A space row drills into its
// directory tree (local state — a folder is not worth a URL).
export function QvdFootprintBySpace({
  runId,
  coverage,
  active,
}: {
  runId: string;
  coverage: QvdAnalysisCoverage;
  active: boolean;
}) {
  const { t } = useTranslation("qvd-analysis");
  const total = coverage.footprintTotalBytes;
  const spaces = coverage.footprintBySpace;

  const [drill, setDrill] = useState<{ spaceName: string; prefix: string } | null>(null);

  const rows = useMemo<UsageRow[]>(
    () =>
      spaces.map((s) => ({
        key: s.spaceName,
        name: s.spaceName,
        kind: "space",
        fileCount: s.fileCount,
        bytes: s.bytes,
      })),
    [spaces],
  );

  const headers = useMemo(
    () => ({
      name: t("footprint.cols.space"),
      files: t("footprint.cols.files"),
      size: t("footprint.cols.size"),
      pct: t("footprint.cols.pct"),
    }),
    [t],
  );

  // Always called, even when a space is open: hooks cannot follow the early return.
  const directory = useQuery(
    qvdDirectoriesQueryOptions(runId, drill?.spaceName ?? "", drill?.prefix ?? "", active),
  );

  if (spaces.length === 0) return null;

  // The space list stays mounted (hidden) while a space is open, so its search
  // and sort are still there on the way back.
  const directoryView = drill ? (
    <DirectoryLevel
      spaceName={drill.spaceName}
      prefix={drill.prefix}
      onPrefix={(prefix) => setDrill({ spaceName: drill.spaceName, prefix })}
      onExit={() => setDrill(null)}
      query={directory}
      texts={{
        label: t("tabs.bySpace"),
        path: t("bySpace.path"),
        backToSpaces: t("bySpace.backToSpaces"),
        up: t("bySpace.up"),
        openDir: (name) => t("bySpace.openDir", { name }),
        error: t("bySpace.error"),
        emptyDir: t("bySpace.emptyDir"),
        summary: (d) =>
          t("bySpace.dirSummary", { count: d.totalFiles, size: formatBytes(d.totalBytes) }),
        truncated: (count) => t("bySpace.truncated", { count }),
        headers: { ...headers, name: t("bySpace.cols.name") },
      }}
    />
  ) : null;

  return (
    <>
      {directoryView}
      <div className="flex flex-col gap-2" hidden={drill !== null}>
        <p className="text-sm text-dim">
          {t("bySpace.summary", { count: spaces.length, size: formatBytes(total) })}
        </p>
        <UsageTable
          label={t("tabs.bySpace")}
          rows={rows}
          total={total}
          headers={headers}
          emptyTitle={t("bySpace.emptyDir")}
          openLabel={(name) => t("bySpace.openSpace", { name })}
          onOpen={(row) => setDrill({ spaceName: row.name, prefix: "" })}
          searchPlaceholder={t("bySpace.searchPlaceholder")}
        />
      </div>
    </>
  );
}
